import { React, useState, useEffect, useMemo } from 'react';
import {
  MotifHeader,
  MotifVerticalNavigationHeaderButton as MotifVerticalNavHeaderButton,
  MotifMenuItem,
  MotifMenu,
  MotifTooltip,
} from '@ey-xd/motif-react';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import ey_Logo from '@assets/icons/ey_Logo.png';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { toTitleCase } from '@util/commonUtil';
import { NAV_LINKS } from '@constants/navbarConstants';
import {
  Current_Page_Name,
  Is_Read_Only_State,
  Modal_Trigger_State,
  PursuitLeaderTableRowDataState,
  System_Date_Time_State,
  Open_Super_User_Modal_State,
} from '@recoil/recoil';
import ExitConformationModal from '@organisms/modals/exitConformationModal';
import { isMobile } from 'react-device-detect';
import UserProfileDropdown from '@organisms/headerComponent/userProfileDropdown';
import '@styles/commonStyles.css';
import { PAGES_NAME } from '../../../constants/commonConstants';
import { getCrmUpdateList } from '@util/commonFunctions';
import SuperUserModal from '../modals/superUserModal';

const Navlinks = [
  {
    label: NAV_LINKS.dashboard,
    path: '/',
    isVisibleOnReadOnly: false,
  },
  {
    label: NAV_LINKS.forecast,
    path: '/forecast',
    isVisibleOnReadOnly: true,
  },
];

let currentSetDate;

const Navbar = () => {
  if (localStorage.getItem('isSuperUser') === 'true') {
    const isSuperUserLinkPresent = Navlinks.some(link => link.label === NAV_LINKS.superUser);

    if (!isSuperUserLinkPresent) {
      Navlinks.unshift({
        label: NAV_LINKS.superUser,
        path: '/super-user',
        isVisibleOnReadOnly: true,
      });
    }
  }

  const location = useLocation();
  useEffect(() => {
    setLinkActive(location.pathname);
  }, [location]);

  const [active, setActive] = useState(Navlinks[0].label);
  const [menuVisibility, setMenuVisibility] = useState(false);
  const [isReadOnlyState, setIsReadOnlyState] = useRecoilState(Is_Read_Only_State);
  const [isMslDelegate, setIsMslDelegate] = useState(false);
  const rowData = useRecoilValue(PursuitLeaderTableRowDataState);
  const systemDateTime = useRecoilValue(System_Date_Time_State);
  const [openSuperUserModal, setOpenSuperUserModal] = useRecoilState(Open_Super_User_Modal_State);

  const navigate = useNavigate();

  const navigateToPage = link => {
    setActive(link.label);
    navigate(link.path);
  };

  const setLinkActive = path => {
    const segments = path.split('/');
    const activeLink = toTitleCase(segments[1]);

    if (activeLink === '') {
      setActive(NAV_LINKS.dashboard);
    } else if (activeLink === 'Forecast') {
      setActive('Forecasting');
    } else {
      setActive(activeLink);
    }
  };

  const setShowWarningModal = useSetRecoilState(Modal_Trigger_State);
  const currentPageName = useRecoilValue(Current_Page_Name);

  const [modalNavigate, setModalNavigate] = useState('');

  const dataObjSize = getCrmUpdateList(rowData)?.length;

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  });

  useEffect(async () => {
    if (systemDateTime !== null) {
      currentSetDate = new Date(systemDateTime);
    }
  }, [systemDateTime]);

  useMemo(() => {
    setIsReadOnlyState(localStorage.getItem('isReadOnly') === 'true' ? true : false);
  }, [localStorage.getItem('isReadOnly')]);

  useMemo(() => {
    setIsMslDelegate(localStorage.getItem('mslDelegate') ? true : false);
  }, [localStorage.getItem('mslDelegate')]);

  const handleBeforeUnload = e => {
    if (dataObjSize === 0) {
      return;
    }
    e.preventDefault();
    e.returnValue = '';
  };

  const showConfirmModel = link => {
    if (
      link.label !== NAV_LINKS.forecast &&
      dataObjSize !== 0 &&
      currentPageName === PAGES_NAME.LIVE_FORECAST
    ) {
      setShowWarningModal(true);
    } else {
      navigateToPage(link);
    }
  };

  const onDesktopMenuClick = link => {
    if (link.label === NAV_LINKS.superUser) {
      setOpenSuperUserModal(true);
    } else {
      showConfirmModel(link);
      setModalNavigate(link.path);
      setMenuVisibility(false); // To close the menu overlay
    }
  };

  const onMobileMenuClick = link => {
    setMenuVisibility(false); // To close the menu overlay

    if (link.label === NAV_LINKS.superUser) {
      setOpenSuperUserModal(true);
    } else {
      showConfirmModel(link);
      setModalNavigate(link.path);
    }
  };

  return (
    <>
      <MotifHeader
        className="navbar-header"
        logo={<img src={ey_Logo} alt="EY Logo" />}
        leftComponents={
          <>
            {' '}
            {isMobile ? (
              <MotifVerticalNavHeaderButton
                className="mobile-hamberg-menu"
                onClick={() => {
                  setMenuVisibility(true);
                }}
              />
            ) : null}
          </>
        }
        appHeaderName={
          !isMobile &&
          (process.env.REACT_APP_DATEPICKER_FEATURE_FLAG === 'DEV' ||
            process.env.REACT_APP_DATEPICKER_FEATURE_FLAG === 'UAT' ||
            process.env.REACT_APP_DATEPICKER_FEATURE_FLAG === 'SPSQA') ? (
            <MotifTooltip
              id="bottom"
              trigger={<span>Sales Forecasting</span>}
              allowHover={true}
              allowClick={true}
              placement="bottom"
              hideCloseButton={false}
            >
              {currentSetDate?.toString()}
            </MotifTooltip>
          ) : null
        }
        iconsContainer={
          <>
            {/* Mobile Menu */}
            {isMobile ? (
              <>
                <MotifMenu
                  openFromLeft
                  variant="alt"
                  show={menuVisibility}
                  onClose={() => setMenuVisibility(false)}
                  className="mobile-menu"
                >
                  <MotifMenuItem>
                    <PagesText>PAGES</PagesText>
                  </MotifMenuItem>
                  {isReadOnlyState || isMslDelegate
                    ? Navlinks.map(
                        // eslint-disable-next-line
                        link =>
                          link.isVisibleOnReadOnly ? (
                            <MotifMenuItem
                              key={link.label}
                              active={active === link.label}
                              onClick={() => {
                                onMobileMenuClick(link);
                              }}
                              className="mobile-menu"
                            >
                              <MobileNavLinkText active={active === link.label}>
                                {link.label}{' '}
                              </MobileNavLinkText>
                            </MotifMenuItem>
                          ) : null,
                      )
                    : null}

                  {!isReadOnlyState && !isMslDelegate
                    ? Navlinks.map(link => (
                        <MotifMenuItem
                          key={link.label}
                          active={active === link.label}
                          onClick={() => {
                            onMobileMenuClick(link);
                          }}
                          className="mobile-menu"
                        >
                          <MobileNavLinkText active={active === link.label}>
                            {link.label}{' '}
                          </MobileNavLinkText>
                        </MotifMenuItem>
                      ))
                    : null}
                </MotifMenu>
              </>
            ) : null}

            {/* Desktop Menu */}
            {!isMobile ? (
              <>
                {isReadOnlyState || isMslDelegate
                  ? Navlinks.map(
                      // eslint-disable-next-line
                      link =>
                        link.isVisibleOnReadOnly ? (
                          <Tab
                            key={link.label}
                            active={active === link.label}
                            onClick={() => {
                              onDesktopMenuClick(link);
                            }}
                          >
                            {link.label}
                          </Tab>
                        ) : null,
                    )
                  : null}

                {!isReadOnlyState && !isMslDelegate
                  ? Navlinks.map(link => (
                      <Tab
                        key={link.label}
                        active={active === link.label}
                        onClick={() => {
                          onDesktopMenuClick(link);
                        }}
                      >
                        {link.label}
                      </Tab>
                    ))
                  : null}
              </>
            ) : null}
            {/* <ProfileDropdown /> */}
            <UserProfileDropdown />
            <ExitConformationModal modalNavigate={modalNavigate} />
            {openSuperUserModal ? <SuperUserModal /> : null}
          </>
        }
      />
    </>
  );
};

// Shift this styled component to style file
export const Tab = styled.button`
  padding: 30px 20px 8px 20px;
  cursor: pointer;
  color: white;
  background-color: transparent;
  border: 0;
  outline: 0;
  border-bottom: 2px solid transparent;
  transition: ease border-bottom 150ms;
  ${({ active }) =>
    active &&
    `
    border-bottom: 2px solid white;
  `}
  &:focus {
    border: 1px solid white;
  }
`;

export const MobileNavLinkText = styled.div`
  padding: 10px 0px 0px 20px;

  ${({ active }) =>
    active &&
    `
  color: #ffe600;
`}
`;

export const PagesText = styled.div`
  padding: 10px 0px 10px 20px;
  background-color: #2e2e38;
`;

export default Navbar;
